import { collectServiceLines, isServiceLine } from "./dtoolsReviewNavigation";
import type { DtoolsReviewSystem } from "./dtoolsStagingTransform";
import type { DtoolsStagingLine } from "./dtoolsJsonStaging";
import {
  resolveProposalLineCustomerDescription,
  resolveProposalLinePresentation,
  resolveProposalSupportingComponentLabel,
} from "./proposalLinePresentation";
import { formatDtoolsPdfPackageDate } from "./dtoolsPdfPackage";

export type DtoolsPdfSupportingComponent = {
  id: string;
  label: string;
  qtyLabel: string;
  /** Present when the accessory carries a stored price (nested only — never a separate priced row). */
  unitPrice?: number;
  lineTotal?: number;
};

export type DtoolsPdfScheduleLine = {
  id: string;
  system: string;
  room: string;
  brand: string;
  /** Schedule Model / Product Title column — never the body description. */
  model: string;
  /** Customer-facing description — once only; never duplicated into `model`. */
  description: string;
  /** Exact stored display name when present (renderer only — never invented). */
  displayName: string;
  /** Canonical customer title (parity with interactive proposal). */
  customerTitle: string;
  qtyLabel: string;
  /** PDF presentation Unit Price (bundles supporting-component cost when nested). */
  unitPrice: number;
  /** PDF presentation Line Total (bundles supporting-component cost when nested). */
  lineTotal: number;
  isService: boolean;
  /** @deprecated Supporting components are always nested — never separate priced rows. */
  isSupportingComponent?: boolean;
  /** Nested accessories — name + qty only (prices rolled into parent Unit/Line). */
  supportingComponents: DtoolsPdfSupportingComponent[];
};

/**
 * @deprecated Bundle pricing is always applied. Option retained for call-site compatibility.
 */
export type BuildDtoolsPdfScheduleOptions = {
  /** @deprecated Ignored — priced supporting components stay nested and are bundled into the parent. */
  expandPricedSupportingComponents?: boolean;
};

/** Exact customer-facing note appended to parent Description when supporting components nest. */
export const INCLUDING_SUPPORTING_COMPONENTS_NOTE = "* Including supporting components";

/** Exact VO change note — one occurrence per VO per printed row. */
export function formatVoIncludesChangesNote(input: {
  voNumber?: string | null;
  date?: string | Date | null;
}): string | null {
  const voNumber = String(input.voNumber ?? "").trim();
  if (!voNumber) return null;
  const dateLabel = input.date ? formatDtoolsPdfPackageDate(input.date) : "";
  if (dateLabel) return `* Includes changes from ${voNumber}, ${dateLabel}`;
  return `* Includes changes from ${voNumber}`;
}

function clean(value: string | null | undefined): string {
  return String(value ?? "").trim();
}

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

function formatQtyLabel(line: DtoolsStagingLine): string {
  const qty = Number(line.quantity);
  const n = Number.isFinite(qty) ? qty : 0;
  if (Number.isInteger(n)) return String(n);
  return String(n);
}

function isRemovedSupportingComponent(line: DtoolsStagingLine): boolean {
  return line.variationOrderAnnotation?.kind === "removed";
}

/** Accessory whose lineTotal contributes to the parent bundled PDF price (non-removed, non-service). */
export function isBillableSupportingComponentForBundle(line: DtoolsStagingLine): boolean {
  if (isServiceLine(line)) return false;
  if (isRemovedSupportingComponent(line)) return false;
  return true;
}

/** @deprecated Prefer isBillableSupportingComponentForBundle — name kept for call-site migration. */
export function isPricedSupportingComponent(line: DtoolsStagingLine): boolean {
  if (!isBillableSupportingComponentForBundle(line)) return false;
  return Math.abs(roundMoney(Number(line.lineTotal) || 0)) > 0.005;
}

/** Σ supporting lineTotals included in the parent bundled Line Total (excludes removed / services). */
export function sumBillableSupportingLineTotals(accessories: DtoolsStagingLine[]): number {
  let sum = 0;
  for (const acc of accessories) {
    if (!isBillableSupportingComponentForBundle(acc)) continue;
    sum += roundMoney(Number(acc.lineTotal) || 0);
  }
  return roundMoney(sum);
}

/**
 * PDF presentation only — does not mutate stored proposal lines.
 * bundledLineTotal = primary.lineTotal + Σ(non-removed supporting lineTotals)
 * bundledUnitPrice = bundledLineTotal / primary.quantity
 */
export function computeBundledPrimaryPricing(
  primary: DtoolsStagingLine,
  accessories: DtoolsStagingLine[],
): { bundledLineTotal: number; bundledUnitPrice: number; supportingSum: number } {
  const primaryLine = roundMoney(Number(primary.lineTotal) || 0);
  const supportingSum = sumBillableSupportingLineTotals(accessories);
  const bundledLineTotal = roundMoney(primaryLine + supportingSum);
  const qty = Number(primary.quantity);
  const safeQty = Number.isFinite(qty) && Math.abs(qty) > 0.000001 ? qty : 1;
  const bundledUnitPrice = roundMoney(bundledLineTotal / safeQty);
  return { bundledLineTotal, bundledUnitPrice, supportingSum };
}

function appendIncludingSupportingNote(description: string): string {
  const base = clean(description);
  if (base.includes(INCLUDING_SUPPORTING_COMPONENTS_NOTE)) return base;
  if (!base) return INCLUDING_SUPPORTING_COMPONENTS_NOTE;
  return `${base}\n${INCLUDING_SUPPORTING_COMPONENTS_NOTE}`;
}

function appendDescriptionNote(description: string, note: string): string {
  const base = clean(description);
  const trimmedNote = clean(note);
  if (!trimmedNote) return base;
  if (base.includes(trimmedNote)) return base;
  if (!base) return trimmedNote;
  return `${base}\n${trimmedNote}`;
}

/** Active VO annotations on the printed parent (primary + nested supporting components). */
export function collectVoIncludesChangesNotes(
  primary: DtoolsStagingLine,
  accessories: DtoolsStagingLine[] = [],
): string[] {
  const byVo = new Map<string, string>();
  const consider = (line: DtoolsStagingLine) => {
    const ann = line.variationOrderAnnotation;
    if (!ann) return;
    if (ann.kind !== "added" && ann.kind !== "quantity_changed") return;
    const voNumber = String(ann.voNumber ?? "").trim();
    if (!voNumber || byVo.has(voNumber)) return;
    const note = formatVoIncludesChangesNote({ voNumber, date: ann.date });
    if (note) byVo.set(voNumber, note);
  };
  consider(primary);
  for (const acc of accessories) {
    if (isServiceLine(acc)) continue;
    consider(acc);
  }
  return Array.from(byVo.values());
}

/** Same customer-facing description as Link A / Link B — no PDF rewrite. */
export function resolvePdfScheduleDescription(line: DtoolsStagingLine): string {
  return resolveProposalLineCustomerDescription(line);
}

/** Exact stored display name — empty when none was saved (no inference). */
export function resolvePdfScheduleDisplayName(line: DtoolsStagingLine): string {
  return clean(line.displayName);
}

/** Compact label for a nested supporting component — name only, never body description. */
export function resolvePdfSupportingComponentLabel(line: DtoolsStagingLine): string {
  return resolveProposalSupportingComponentLabel(line);
}

function toSupportingComponents(accessories: DtoolsStagingLine[]): DtoolsPdfSupportingComponent[] {
  const out: DtoolsPdfSupportingComponent[] = [];
  for (const acc of accessories) {
    if (isServiceLine(acc)) continue;
    out.push({
      id: acc.id,
      label: resolveProposalSupportingComponentLabel(acc),
      qtyLabel: formatQtyLabel(acc),
      unitPrice: roundMoney(Number(acc.unitPrice) || 0),
      lineTotal: roundMoney(Number(acc.lineTotal) || 0),
    });
  }
  return out;
}

function pushPrimaryLine(
  out: DtoolsPdfScheduleLine[],
  systemLabel: string,
  roomLabel: string,
  line: DtoolsStagingLine,
  accessories: DtoolsStagingLine[],
): void {
  const nestedAccessories = accessories.filter((acc) => !isServiceLine(acc));
  const presentation = resolveProposalLinePresentation(line, { roomLabel });
  const nested = toSupportingComponents(nestedAccessories);
  const { bundledLineTotal, bundledUnitPrice, supportingSum } = computeBundledPrimaryPricing(
    line,
    nestedAccessories,
  );

  let description = presentation.customerDescription;
  let unitPrice = presentation.unitPrice;
  let lineTotal = presentation.lineTotal;
  if (nested.length > 0) {
    description = appendIncludingSupportingNote(description);
    lineTotal = bundledLineTotal;
    if (supportingSum > 0.005) {
      unitPrice = bundledUnitPrice;
    }
  }
  // VO notes land on the printed parent (including when a nested supporting component changed).
  for (const note of collectVoIncludesChangesNotes(line, nestedAccessories)) {
    description = appendDescriptionNote(description, note);
  }

  out.push({
    id: line.id,
    system: systemLabel,
    room: roomLabel,
    brand: presentation.brandLabel,
    model: presentation.scheduleTitle,
    description,
    displayName: clean(line.displayName),
    customerTitle: presentation.customerTitle,
    qtyLabel: presentation.qtyLabel,
    unitPrice,
    lineTotal,
    isService: presentation.isService,
    supportingComponents: nested,
  });
}

function pushServiceLine(
  out: DtoolsPdfScheduleLine[],
  systemLabel: string,
  line: DtoolsStagingLine,
): void {
  const presentation = resolveProposalLinePresentation(line, { roomLabel: "Services" });
  let description = presentation.customerDescription;
  for (const note of collectVoIncludesChangesNotes(line, [])) {
    description = appendDescriptionNote(description, note);
  }
  out.push({
    id: line.id,
    system: systemLabel,
    room: "Services",
    brand: presentation.brandLabel,
    model: presentation.customerTitle,
    description,
    displayName: clean(line.displayName),
    customerTitle: presentation.customerTitle,
    qtyLabel: presentation.qtyLabel,
    unitPrice: presentation.unitPrice,
    lineTotal: presentation.lineTotal,
    isService: true,
    supportingComponents: [],
  });
}

/**
 * Flatten one system into a quotation schedule.
 * Supporting components always nest under the primary. Their billable lineTotals are
 * bundled into the parent Unit Price and Line Total (PDF presentation only).
 * Services appear at the end under room label "Services".
 */
export function buildDtoolsPdfScheduleLinesForSystem(
  system: DtoolsReviewSystem,
  systemLabel: string,
  _options?: BuildDtoolsPdfScheduleOptions,
): DtoolsPdfScheduleLine[] {
  const lines: DtoolsPdfScheduleLine[] = [];
  const serviceLines: DtoolsStagingLine[] = [];

  for (const room of system.rooms) {
    const roomName = clean(room.name) || "Room";
    for (const block of room.primaries) {
      if (isServiceLine(block.line)) {
        serviceLines.push(block.line);
      } else {
        for (const acc of block.accessories) {
          if (isServiceLine(acc)) serviceLines.push(acc);
        }
        pushPrimaryLine(lines, systemLabel, roomName, block.line, block.accessories);
      }
    }
  }

  for (const block of system.systemLevelPrimaries) {
    if (isServiceLine(block.line)) {
      serviceLines.push(block.line);
    } else {
      for (const acc of block.accessories) {
        if (isServiceLine(acc)) serviceLines.push(acc);
      }
      pushPrimaryLine(lines, systemLabel, "Project-Wide", block.line, block.accessories);
    }
  }

  for (const room of system.rooms) {
    for (const svc of collectServiceLines(room.primaries)) {
      if (!serviceLines.some((existing) => existing.id === svc.id)) serviceLines.push(svc);
    }
  }
  for (const svc of collectServiceLines(system.systemLevelPrimaries)) {
    if (!serviceLines.some((existing) => existing.id === svc.id)) serviceLines.push(svc);
  }

  for (const svc of serviceLines) {
    pushServiceLine(lines, systemLabel, svc);
  }

  return lines;
}

/** Σ of schedule row lineTotals (authoritative presented amounts on each printed/model row). */
export function sumDtoolsPdfScheduleLineTotals(lines: Array<{ lineTotal: number }>): number {
  return roundMoney(lines.reduce((sum, line) => sum + (Number(line.lineTotal) || 0), 0));
}

/** Blank repeated room labels for schedule display (BOM-style). */
export function blankRepeatedScheduleRooms<T extends { room: string }>(
  lines: T[],
): Array<T & { roomDisplay: string }> {
  let lastRoom = "";
  return lines.map((line) => {
    const room = clean(line.room);
    const roomDisplay = room && room === lastRoom ? "" : room;
    if (room) lastRoom = room;
    return { ...line, roomDisplay };
  });
}
